import { useSyncExternalStore } from "react";
import type { CityConfig } from "../../../config/cities.ts";
import { cityAt } from "./city.ts";

/**
 * The visitor's city: picked in the header, or found once by the position on the first visit. One key
 * for the whole site (every city's map is on the same origin); its value is a city id.
 */
export const CITY_CHOICE_KEY = "atl.city";
let memoryChoice: string | null = null;

export function storedCity(): string | null {
  try {
    return localStorage.getItem(CITY_CHOICE_KEY);
  } catch {
    return memoryChoice;
  }
}
export function rememberCity(id: string): void {
  memoryChoice = id;
  try {
    localStorage.setItem(CITY_CHOICE_KEY, id);
  } catch {
    /* Storage may be disabled. */
  }
}

/**
 * The site's front door: the bare root, as typed or opened from a search result. A city's own path, a
 * route, a call or a line in the URL is a link someone chose, and it opens as it is.
 */
export function isEntry(location: { pathname: string; search: string; hash: string }): boolean {
  if (location.pathname !== "/" || location.hash !== "") return false;
  const params = new URLSearchParams(location.search);
  params.delete("preview");
  return params.toString() === "";
}
/** Read before the App rewrites the URL state (it always writes the layers). */
export const ENTRY = typeof window === "undefined" ? false : isEntry(window.location);

/** After the first rewrite: a route, a call, a line or another tab means the visitor is at work. */
export function isUntouched(search: string): boolean {
  const params = new URLSearchParams(search);
  return !["from", "to", "call", "line", "stop", "tab"].some((key) => params.has(key));
}

export type CityOutcome =
  | { kind: "here" }
  | { kind: "elsewhere"; city: CityConfig }
  | { kind: "unsupported" };
/** Where the position puts the visitor among the cities they may open. */
export function cityOutcome(
  point: { lat: number; lon: number },
  cities: readonly CityConfig[],
  currentId: string,
): CityOutcome {
  const city = cityAt(point, cities);
  if (city === null) return { kind: "unsupported" };
  return city.id === currentId ? { kind: "here" } : { kind: "elsewhere", city };
}

/**
 * The city card: another city found after the visitor started working (a question, not a jump), no
 * map for the place they are in, or "my city is missing" from the header's menu.
 */
export type CityNotice =
  | { kind: "elsewhere"; city: CityConfig }
  | { kind: "unsupported" }
  | { kind: "missing" };
let notice: CityNotice | null = null;
const listeners = new Set<() => void>();
export function showCityNotice(next: CityNotice | null): void {
  notice = next;
  for (const listener of listeners) listener();
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function useCityNotice(): CityNotice | null {
  return useSyncExternalStore(
    subscribe,
    () => notice,
    () => null,
  );
}
